import type { DeclineGuidance, MerchantInvoice } from '@order-up/models';
/**
 * Payments API Client Service
 * Provider-neutral card operations against our own `/api/v1/payments` routes.
 * The server resolves the processor from the location's `providerRouting`;
 * the wire shapes (`retref`, `respstat`, `respcode`, `amount`, …) are OUR
 * API's response contract, not any processor's.
 */

import {
    // Auth models
    AuthResponse,
    Neutral,
    // Capture models
    CaptureResponse,
    // Inquire models
    InquireByOrderidResponse,
    InquireResponse,
    // Void models
    VoidResponse,
    type IAuthRequest,
    type ICaptureRequest,
    type IInquireByOrderidRequest,
    type IInquireRequest,
} from '@order-up/models';

import {
    // Auth schemas
    AuthRequestSchema,
    AuthResponseSchema,

    // Capture schemas
    CaptureRequestSchema,
    CaptureResponseSchema,

    // Inquire schemas
    InquireByOrderidRequestSchema,
    InquireByOrderidResponseSchema,
    InquireRequestSchema,
    InquireResponseSchema,

    // Void schemas
    VoidResponseSchema
} from '@order-up/schemas';

import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('PaymentsAPI');

const apiClient = createApiClient('/api/v1/payments');

// ========================================
// Authorization Methods
// ========================================

/**
 * Process payment authorization
 */
const authorize = async (request: IAuthRequest): Promise<AuthResponse> => {
    // Validate request with schema
    const validatedRequest = AuthRequestSchema.parse(request);
    logger.debug(' Authorization request:', validatedRequest);

    const response = await apiClient.post('/auth', validatedRequest);

    // Validate and parse response with schema
    const validatedResponse = AuthResponseSchema.parse(response);
    return AuthResponse.fromJSON(validatedResponse);
};

/**
 * Process payment authorization using the neutral AuthorizeRequest schema.
 *
 * Phase 3: Sends the provider-agnostic AuthorizeRequest to the Phase 1 /auth
 * endpoint. The server dispatches to the location's processor from its
 * providerRouting config. Response is still the wire AuthResponse shape for
 * backward-compat with POS / web consumers.
 *
 * Use this function for new callers. Legacy callers should migrate from
 * `authorize()` (IAuthRequest) to this function.
 */
const neutralAuthorize = async (request: Neutral.AuthorizeRequest): Promise<AuthResponse> => {
    logger.debug('Neutral authorization request', { orderId: request.orderId });
    // The neutral shape goes directly to /auth — no Zod schema validation on
    // the client side for the neutral body because the server-side AJV schema
    // is authoritative (avoids double-validation skew).
    const response = await apiClient.post('/auth', request);
    const validatedResponse = AuthResponseSchema.parse(response);
    return AuthResponse.fromJSON(validatedResponse);
};

// ========================================
// Capture Methods
// ========================================

/**
 * Capture authorized payment for settlement
 */
const capture = async (request: ICaptureRequest): Promise<CaptureResponse> => {
    // Validate request with schema
    const validatedRequest = CaptureRequestSchema.parse(request);
    logger.debug(' Capture request:', validatedRequest);

    const response = await apiClient.post('/capture', validatedRequest);

    // Validate and parse response with schema
    const validatedResponse = CaptureResponseSchema.parse(response);
    return CaptureResponse.fromJSON(validatedResponse);
};

// ========================================
// Tip Adjustment Methods
// ========================================

/**
 * Request for POST /payments/tip-adjust.
 *
 * `tipAmount` is the NEW tip in dollars and REPLACES the previous tip
 * (it is not additive). The server re-captures the transaction at
 * base + tipAmount before settlement and updates the payment record.
 */
export interface ITipAdjustRequest {
    /** Processor reference (TRX Reference.Guid) from the original auth. */
    retref: string;
    /** New tip amount in dollars — replaces the previous tip. */
    tipAmount: number;
    tenantId: number;
    restaurantId: number;
    /**
     * 16-128 char idempotency key. Mint ONCE per user intent
     * (dialog-open) and reuse on retries of the same intent.
     */
    idempotencyKey: string;
}

/** Capture-shaped tip-adjust response (subset serialized by the API route). */
export interface ITipAdjustResponse {
    merchid?: string;
    retref?: string;
    /** New total (base + adjusted tip) as a dollar string. */
    amount?: string;
    /** A=Approved, B=Retry, C=Declined */
    respstat?: string;
    resptext?: string;
    respcode?: string;
    /** Settlement status */
    setlstat?: string;
}

/**
 * Adjust the tip on a captured transaction before settlement.
 *
 * The idempotency key is sent both as the `Idempotency-Key` header and in
 * the body (the strict idempotency middleware accepts either; sending both
 * matches the header-first contract while staying consistent with the
 * void/refund body convention).
 */
const tipAdjust = async (request: ITipAdjustRequest): Promise<ITipAdjustResponse> => {
    logger.debug('Tip adjust request', { retref: request.retref });

    const response = await apiClient.post('/tip-adjust', request, {
        headers: { 'Idempotency-Key': request.idempotencyKey },
    });

    // No client-side Zod parse — the route's response schema is authoritative
    // and serializes exactly the capture-shaped subset above.
    return response as unknown as ITipAdjustResponse;
};

// Saved cards (card on file) live in the provider-agnostic card vault
// (`/payments/vault/*`) + the customer-facing saved-cards API
// (`/customers/me/saved-cards`). Use `savedCardsService` / `useSavedCards`.

// ========================================
// Inquire Methods
// ========================================

/**
 * Inquire transaction by retrieval reference number.
 *
 * The neutral GET /payments/inquire/:retref route requires `tenantId` and
 * `restaurantId` query params so the server can resolve the issuing provider
 * from the PaymentRecord. The response is our neutral inquire shape and
 * includes `voidable` ('Y'|'N'), `refundable` ('Y'|'N') and `setlstat`.
 */
const inquire = async (
    request: IInquireRequest & { tenantId?: number; restaurantId?: number }
): Promise<InquireResponse> => {
    const { tenantId, restaurantId: locationNumericId, ...inquireRequest } = request;
    // Validate request with schema
    const validatedRequest = InquireRequestSchema.parse(inquireRequest);
    logger.debug(' Inquire request:', validatedRequest);

    // Provider-dispatch context travels as query params (required by the
    // neutral route's querystring schema).
    const queryParams = new URLSearchParams();
    if (tenantId !== undefined) queryParams.append('tenantId', String(tenantId));
    if (locationNumericId !== undefined) queryParams.append('restaurantId', String(locationNumericId));
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await apiClient.get(`/inquire/${validatedRequest.retref}${query}`);

    // Validate and parse response with schema
    const validatedResponse = InquireResponseSchema.parse(response);
    return InquireResponse.fromJSON(validatedResponse);
};

/**
 * Inquire transaction by order ID. Like `inquire`, the neutral route requires
 * `tenantId` + `restaurantId` query params for provider routing.
 */
const inquireByOrderId = async (
    request: IInquireByOrderidRequest & { tenantId?: number; restaurantId?: number }
): Promise<InquireByOrderidResponse> => {
    const { tenantId, restaurantId: locationNumericId, ...inquireRequest } = request;
    // Validate request with schema
    const validatedRequest = InquireByOrderidRequestSchema.parse(inquireRequest);
    logger.debug(' Inquire by order ID request:', validatedRequest);

    const queryParams = new URLSearchParams();
    if (tenantId !== undefined) queryParams.append('tenantId', String(tenantId));
    if (locationNumericId !== undefined) queryParams.append('restaurantId', String(locationNumericId));
    if (validatedRequest.set !== undefined) {
        queryParams.append('set', validatedRequest.set);
    }

    const url = `/inquireByOrderid/${validatedRequest.orderid}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);

    // Validate and parse response with schema
    const validatedResponse = InquireByOrderidResponseSchema.parse(response);
    return InquireByOrderidResponse.fromJSON(validatedResponse);
};

// ========================================
// Settlement Batch Methods
// ========================================

/** A single settlement batch over the wire (mirrors models' ISettlementBatch). */
export interface IBatchRecord {
    id: string;
    provider: string;
    name?: string;
    status: 'open' | 'closed' | 'settled' | 'unknown';
    /**
     * The processor's batch type (TRX `BatchType`: `Credit`, `Ach`, …). Absent
     * = a card batch. Read it with `isAchSettlementBatch` (`@order-up/models`).
     */
    batchType?: string;
    manualCloseSupported: boolean;
    openedAt?: string;
    closedAt?: string;
    settledAt?: string;
    approvedCount?: number;
    settledCount?: number;
    transactionCount?: number;
    grossVolumeMinor?: number;
    netDepositMinor?: number;
    closedBy?: string;
    closedFrom?: 'pos' | 'management' | 'scheduled' | 'auto';
    createdAt?: string;
    updatedAt?: string;
}

/** Money sub-object over the wire (minor units + ISO currency) — mirrors PaymentMoney. */
export interface IBatchMoney {
    minorUnits: number;
    currency: string;
}

/**
 * Full per-batch financial detail (GET /payments/batches/:guid → `batch`),
 * mirroring the neutral `SettlementBatchDetail` (TRX §4.5 Batch Lookup).
 *
 * Money fields are PaymentMoney-shaped ({ minorUnits, currency }) — minor units
 * (cents), divide by 100 for display. `status` is the neutral capitalized form
 * (Open|Closed|Settled|Hold|Released), unlike the lowercase list-view status.
 * Every field beyond the core totals is sparse (gateway populates per batch
 * state); the route omits absent ones entirely.
 */
export interface IBatchDetail {
    provider: string;
    providerBatchId: string;
    merchantId?: string;
    /** Neutral lifecycle status: 'Open' | 'Closed' | 'Settled' | 'Hold' | 'Released'. */
    status: string;
    transactionCount: number;
    grossVolume?: IBatchMoney;
    netDeposit?: IBatchMoney;
    tipAmount?: IBatchMoney;
    voidedAmount?: IBatchMoney;
    averageTicket?: IBatchMoney;
    debitCount?: number;
    creditCount?: number;
    batchName?: string;
    batchType?: string;
    dba?: string;
    openedAt?: string;
    closedAt?: string;
    settledAt?: string;
    currency: string;
}

/** Our Books ACH debit attempt behind one TRX ACH entry (never bank numbers). */
export interface IAchAttemptMatch {
    attemptId: string;
    status: string;
    attemptNumber: number;
    amountCents: number;
    invoiceId: string | null;
    /** e.g. `INV-1001`, when the invoice still reads. */
    invoiceNumber: string | null;
    /** The `Reference.PurchaseId` the Sale sent. */
    purchaseId: string;
    returnCode: string | null;
}

/** One entry in an ACH batch (TRX `Ach Report DailyDetail`). Only the last four. */
export interface IAchBatchEntry {
    guid: string;
    tranAction: string;
    amountMinor?: number;
    currencyCode?: string;
    responseCode?: string;
    responseText?: string;
    category?: string;
    /** TRX `Reference.Status` — `Hold` = not yet settled. */
    status?: string;
    tranDate?: string;
    tranTime?: string;
    lastFour?: string;
    purchaseId?: string;
    exceptionCount?: number;
    ours: IAchAttemptMatch | null;
}

/** GET /payments/batches/:guid/ach-entries — an ACH batch's entries, from TRX. */
export interface IAchBatchEntries {
    batchGuid: string;
    /** The UTC transaction-date window TRX was asked for (ISO). */
    window: { from: string; to: string };
    rows: IAchBatchEntry[];
    /** TRX capped the answer (X1): the rows are real but incomplete. */
    partial: boolean;
}

/** Live current-batch status reconciled against the processor. */
export interface IBatchStatusResponse {
    provider: string;
    manualCloseSupported: boolean;
    approvedCount: number;
    settledCount: number;
    message?: string;
    batch: {
        id?: string;
        name?: string;
        openedAt?: string;
        closedAt?: string;
        settledAt?: string;
        status?: 'open' | 'closed' | 'settled' | 'unknown';
        /**
         * Processor's own batch rollup (TRX Batch Lookup) — distinct from the
         * top-level `approvedCount`/`settledCount`, which count OUR PaymentRecords.
         * `netDepositMinor` is present only once the batch is closed/settled.
         */
        transactionCount?: number;
        grossVolumeMinor?: number;
        netDepositMinor?: number;
    } | null;
}

export interface ICloseBatchResponse {
    provider: string;
    closed: boolean;
    respstat?: string;
    message?: string;
    providerBatchId?: string;
    transactionCount?: number;
    grossVolumeMinor?: number;
    netDepositMinor?: number;
    settledNow?: number;
    fundedNow?: number;
}

/** Current processor batch status (the live "current batch" parity view). */
const getBatchStatus = async (
    tenantId: number,
    locationNumericId: number,
): Promise<IBatchStatusResponse> => {
    const response = await apiClient.get(
        `/batch-status?tenantId=${tenantId}&restaurantId=${locationNumericId}`,
    );
    return response as unknown as IBatchStatusResponse;
};

/** Persisted settlement-batch history (newest first). */
const listBatches = async (
    tenantId: number,
    locationNumericId: number,
    limit = 100,
): Promise<IBatchRecord[]> => {
    const response = await apiClient.get(
        `/batches?tenantId=${tenantId}&restaurantId=${locationNumericId}&limit=${limit}`,
    );
    return (response as unknown as { batches?: IBatchRecord[] })?.batches ?? [];
};

/**
 * Merchant-billing invoices the processor issued over a window.
 *
 * The other side of the fee reconciliation: everything else on the Deposits &
 * Fees screen is computed from ledger rows, and this is what the processor says
 * it CHARGED. Read live from the gateway, so an error here is a real error —
 * callers must NOT coerce a failure into an empty list, because "you were
 * billed nothing" and "we could not reach the processor" are opposite claims.
 */
const listMerchantInvoices = async (
    tenantId: number,
    locationNumericId: number,
    fromDate: string,
    toDate: string,
): Promise<MerchantInvoice[]> => {
    const response = await apiClient.get(
        `/invoices?tenantId=${tenantId}&restaurantId=${locationNumericId}` +
            `&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`,
    );
    return (response as unknown as { invoices?: MerchantInvoice[] })?.invoices ?? [];
};

/**
 * Full per-batch financial detail for one batch Guid (TRX §4.5 Batch Lookup).
 * Returns `null` when the processor has no per-batch lookup or when the
 * gateway has no such batch.
 */
const getBatchDetail = async (
    tenantId: number,
    locationNumericId: number,
    guid: string,
): Promise<IBatchDetail | null> => {
    const response = await apiClient.get(
        `/batches/${encodeURIComponent(guid)}?tenantId=${tenantId}&restaurantId=${locationNumericId}`,
    );
    return (response as unknown as { batch?: IBatchDetail | null })?.batch ?? null;
};

/**
 * The entries in ONE ACH batch, from TRX, each matched to our Books debit
 * attempt. Throws on a card batch (409 `BATCH_NOT_ACH`) and when the batch is
 * unknown (404) — never an empty list for "could not ask".
 */
const getBatchAchEntries = async (
    tenantId: number,
    locationNumericId: number,
    guid: string,
): Promise<IAchBatchEntries> => {
    const response = await apiClient.get(
        `/batches/${encodeURIComponent(guid)}/ach-entries?tenantId=${tenantId}&restaurantId=${locationNumericId}`,
    );
    return response as unknown as IAchBatchEntries;
};

/**
 * Manually close a batch. With an
 * explicit `providerBatchId` closes THAT batch (any open/hold batch from the
 * Batches list); absent closes the merchant's current open batch.
 */
const closeBatch = async (params: {
    tenantId: number;
    restaurantId: number;
    captureOpenAuths?: boolean;
    providerBatchId?: string;
    closedBy?: string;
    closedFrom?: 'pos' | 'management' | 'scheduled' | 'auto';
}): Promise<ICloseBatchResponse> => {
    const response = await apiClient.post('/close-batch', {
        captureOpenAuths: false,
        ...params,
    });
    return response as unknown as ICloseBatchResponse;
};

// ========================================
// Neutral Void / Refund (provider-agnostic)
// ========================================
//
// Post the neutral body the /void & /refund routes expect — the processor
// reference plus the tenant/location the server routes on.

/** Result of a neutral post-auth op (void/refund). `respstat:'A'` = approved. */
export interface INeutralPostAuthResult {
    respstat?: string;
    retref?: string;
    amount?: string;
    authcode?: string | number;
    respcode?: string;
    resptext?: string;
    setlstat?: string;
    /**
     * Server-resolved decline guidance on a non-approved post-auth response
     * (capture / void / refund / tip-adjust). Attached at the post-auth
     * convergence point and declared on `neutralPostAuthResponse`, so it
     * survives serialization. Prefer it over `resptext` when telling a human
     * what happened.
     */
    decline?: DeclineGuidance;
}

/** Void a captured/authorized payment (pre-settlement). `amount` omitted = full. */
const voidPayment = async (params: {
    retref: string;
    tenantId: number;
    restaurantId: number;
    amount?: string;
    voidReasonId?: number;
    voidReasonText?: string;
    authorizedBy?: number;
}): Promise<INeutralPostAuthResult> => {
    const response = await apiClient.post('/void', params);
    return response as unknown as INeutralPostAuthResult;
};

/** Refund a settled payment. `amount` omitted = full refund. */
const refundPayment = async (params: {
    retref: string;
    tenantId: number;
    restaurantId: number;
    amount?: string;
    idempotencyKey?: string;
}): Promise<INeutralPostAuthResult> => {
    const { idempotencyKey, ...body } = params;
    const response = await apiClient.post(
        '/refund',
        body,
        idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
    );
    return response as unknown as INeutralPostAuthResult;
};

/**
 * Request for POST /payments/tip-correction — lower the tip on a CAPTURED card
 * payment by returning only the difference to the card
 * (docs/POST_PAYMENT_ADJUSTMENT.md §7). `tipAmount` is the CORRECTED tip the
 * guest should end up paying, not the difference. Manager-gated like refunds.
 */
export interface ITipCorrectionRequest {
    /** Reference of the captured payment (the sale, never a refund child). */
    retref: string;
    /** The corrected tip, in dollars. */
    tipAmount: number;
    tenantId: number;
    restaurantId: number;
    /** Mint ONCE per dialog-open; reuse on retries of the same intent. */
    idempotencyKey: string;
}

/** The Return, plus what it did to the tip (decimal-dollar strings). */
export interface ITipCorrectionResponse extends INeutralPostAuthResult {
    tipCorrection?: {
        /** False when the processor declined the Return — nothing moved. */
        approved: boolean;
        tipReturned: string;
        tipBefore: string;
        tipAfter: string;
    };
}

/**
 * Correct a captured payment's tip. Refusals arrive as 409s carrying a `code`:
 * TIP_CORRECTION_OPEN_HOLD (use tip-adjust), TIP_CORRECTION_INCREASE (charge the
 * difference as a new payment), TIP_CORRECTION_NOT_CORRECTABLE,
 * TIP_CORRECTION_AFTER_SETTLEMENT (this processor returns only after the batch).
 */
const tipCorrection = async (request: ITipCorrectionRequest): Promise<ITipCorrectionResponse> => {
    logger.debug('Tip correction request', { retref: request.retref });
    const { idempotencyKey, ...body } = request;
    const response = await apiClient.post('/tip-correction', body, {
        headers: { 'Idempotency-Key': idempotencyKey },
    });
    return response as unknown as ITipCorrectionResponse;
};

export const paymentsApiService = {
    // Authorization
    authorize,
    neutralAuthorize,

    // Capture
    capture,

    // Tip Adjustment (open hold) / Tip Correction (captured)
    tipAdjust,
    tipCorrection,

    // Inquire
    inquire,
    inquireByOrderId,

    // Settlement batches
    getBatchStatus,
    listBatches,
    listMerchantInvoices,
    getBatchDetail,
    getBatchAchEntries,
    closeBatch,

    // Neutral void / refund
    voidPayment,
    refundPayment,
};
